import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';

const ThumbnailSidebar: React.FC<{
  steps: { id: number; label: string; elementId: string }[];
  stepStatuses: Record<number, 'complete' | 'camel' | 'default'>;
  onStepClick: (stepId: number, elementId: string) => void;
}> = ({ steps, stepStatuses, onStepClick }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: 120, // increased width so text and padding fit
        flexShrink: 0,
        mr: 2,
        ml: -2, // Pull left further to reduce gap
        pt: 1,
        pr: 3, // Extra right padding so text doesn't touch divider
        borderRight: `1px solid ${theme.palette.grey[300]}`,
        maxHeight: 'calc(100vh - 48px)',
        overflowY: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}
    >
      {steps.map((step, index) => {
        const status = stepStatuses[step.id] || 'default';
        const isComplete = status === 'complete';
        const isCamel = status === 'camel';

        let bgColor = theme.palette.greyLight;
        let textColor = 'text.secondary';
        let borderColor = theme.palette.grey[300];

        if (isComplete) {
          bgColor = theme.palette.mode === 'dark' ? '#1b5e20' : '#e8f5e9'; // High contrast green
          textColor = theme.palette.mode === 'dark' ? '#ffffff' : '#1b5e20';
          borderColor = theme.palette.mode === 'dark' ? '#4caf50' : '#2e7d32';
        } else if (isCamel) {
          bgColor = theme.palette.mode === 'dark' ? '#ff8f00' : '#fff8e1'; // Using a deeper amber/camel
          textColor = theme.palette.mode === 'dark' ? '#000000' : '#f57f17';
          borderColor = theme.palette.mode === 'dark' ? '#ff8f00' : '#f57f17';
        }

        return (
          <Box key={step.id} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box
              onClick={() => onStepClick(step.id, step.elementId)}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                cursor: 'pointer',
                opacity: 0.7,
                transition: 'opacity 0.2s',
                gap: 1, // Gap between circle and label
                mb: index < steps.length - 1 ? 1.5 : 1, // Spacing before divider
                width: '100%',
                '&:hover': { opacity: 1 },
              }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: bgColor,
                  color: textColor,
                  border: `1.5px solid ${borderColor}`,
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 'bold', fontSize: '9px', lineHeight: 1 }}
                >
                  {step.id}
                </Typography>
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: '10px',
                  lineHeight: 1,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: 'text.primary',
                }}
              >
                {step.label}
              </Box>
            </Box>
            {index < steps.length - 1 && (
              <Box
                sx={{
                  width: '100%',
                  height: '1px',
                  mb: 1.5,
                  background: `linear-gradient(90deg, ${theme.palette.grey[300]}, transparent)`, // Fades out to the right
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ThumbnailSidebar;
